import * as styles from "./ExpensesSection.css";

import { useAtomValue } from "jotai";
import { type JSX, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Switch } from "@frontend/components/atoms/Switch";
import { useField } from "@frontend/hooks/form";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { formatAmount } from "@frontend/utils/amount";
import { useLingui } from "@lingui/react/macro";

import { projectForm } from "../stores/editor";
import { plannedExpensesTotal } from "../utils/expenses";
import { PlannedExpensesDialog } from "./PlannedExpensesDialog";
import { Section } from "./Section";

const intervention = projectForm.fields.intervention.fields;

/** What the intervention costs on top of its hours, and what is paid back. */
export function ExpensesSection(): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const refundLunch = useField(intervention.refundLunch);
  const refundRide = useField(intervention.refundRide);

  const expenses = useAtomValue(intervention.plannedExpenses.valueAtom);

  const total = plannedExpensesTotal(expenses);

  const [open, setOpen] = useState(false);

  // The tile the popup grows from.
  const originRef = useRef<DOMRect | null>(null);

  return (
    <Section icon="Wallet" legend={t`Expenses`} gutter={false}>
      <Field>
        <FieldLabel>{t`Planned expenses`}</FieldLabel>

        <FieldContent>
          {/* Read here, written in the popup the button opens. */}
          <Input
            readOnly
            value={
              null === total
                ? 0 === expenses.length
                  ? t`None`
                  : t`To fill in`
                : `${formatAmount(total, locale)} ${t`Frs`}`
            }
            className={styles.summary}
            rightSection={
              <Button
                variant="link"
                size="sm"
                leftSection={<Icon name="ListPlus" size="sm" />}
                onClick={(event) => {
                  originRef.current =
                    event.currentTarget.getBoundingClientRect();
                  setOpen(true);
                }}
              >
                {t`Manage`}
              </Button>
            }
          />
        </FieldContent>
      </Field>

      <Field>
        <FieldLabel>{t`Refunds`}</FieldLabel>

        <FieldContent>
          <div className={styles.refunds}>
            <Switch
              label={t`Meals`}
              checked={refundLunch.value ?? false}
              onCheckedChange={(next) => refundLunch.setValue(next)}
            />

            <Switch
              label={t`Rides`}
              checked={refundRide.value ?? false}
              onCheckedChange={(next) => refundRide.setValue(next)}
            />
          </div>
        </FieldContent>
      </Field>

      {open ? (
        <PlannedExpensesDialog
          originRef={originRef}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </Section>
  );
}
